import React, { useState, useEffect, useRef } from 'react';
import { Stepper, Step } from 'react-form-stepper';
import { useNavigate } from 'react-router-dom';
import PersonalInformation from './PersonalInformation/PersonalInformation';
import EducationalBackground from './EducationalBackground/EducationalBackground';
import ProfessionalExperience from './ProfessionalExperience/ProfessionalExperience';
import Recommendations from './Recommendations/Recommendations';
import FellowshipInformation from './FellowshipInformation/FellowshipInformation';
import AdditionalInformation from './AdditionalInformation/AdditionalInformation';
import PreviewPage from './Preview/PreviewPage';
import ThankYou from './ThankYou';
import ApplicationStatus from '../components/ApplicationStatus';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import constants
import { FORM_STEPS } from '../constants/formSteps';
import { BASE_URL } from '../constants/apiConfig';

// Import utilities
import { mapApplicationData } from '../utils/dataMappers';
import { useFormSubmission } from '../utils/useFormSubmission';

const steps = [...FORM_STEPS];
const stepOrder = [
	'personalInfo',
	'educationalBackground',
	'professionalExperience',
	'fellowshipInformation',
	'recommendations',
	'additionalInformation',
	'preview',
];
const Form = () => {
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState(0);
	const [generalError, setGeneralError] = useState('');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [applicationStatus, setApplicationStatus] = useState(null);
	const [resumeData, setResumeData] = useState(null);
	const [applicationId, setApplicationId] = useState(() => {
		// Try to get applicationId from sessionStorage first
		const savedId = sessionStorage.getItem('applicationId');

		return savedId || null; // Start with null for new applications
	});

	// setTimeout(() => {
	//   toast.dismiss();
	// }, 3000);
	const [fullPageLoader, setFullPageLoader] = useState(false);

	// Store temporary data for each step
	const [stepData, setStepData] = useState({
		personalInfo: {},
		educationalBackground: {},
		professionalExperience: {},
		recommendations: {
			recommendation1: {},
			recommendation2: {},
		},
		fellowshipInformation: {},
		additionalInformation: {},
	});

	console.log('Current stepData:', stepData);

	// Store original loaded data as backup for file URLs
	const [originalLoadedData, setOriginalLoadedData] = useState({});

	// Track which steps have been modified since last save
	const [modifiedSteps, setModifiedSteps] = useState(new Set());

	// Debug function - you can call this from browser console: window.debugFormData()
	// React.useEffect(() => {
	//   window.debugFormData = () => {
	//     console.log("🔍 Current stepData:", stepData);
	//     console.log("🔍 Original loaded data:", originalLoadedData);
	//     console.log(
	//       "🔍 Education data in stepData:",
	//       stepData.educationalBackground
	//     );
	//     console.log(
	//       "🔍 Education data in originalLoadedData:",
	//       originalLoadedData.educationalBackground
	//     );
	//     if (stepData.educationalBackground) {
	//       console.log(
	//         "🔍 Certificate URL in stepData:",
	//         stepData.educationalBackground.file
	//       );
	//     }
	//     if (originalLoadedData.educationalBackground) {
	//       console.log(
	//         "🔍 Certificate URL in originalLoadedData:",
	//         originalLoadedData.educationalBackground.file
	//       );
	//     }
	//   };
	// }, [stepData, originalLoadedData]);

	const personalInfoRef = useRef();
	const educationRef = useRef();
	const professionalExperienceRef = useRef();
	const recommendationsRef = useRef();
	const fellowshipInfoRef = useRef();
	const additionalInformationRef = useRef();
	const previewRef = useRef();

	// Use form submission hook
	const {
		isSaving: hookIsSaving,
		fullPageLoader: hookFullPageLoader,
		saveForm,
		submitForm,
	} = useFormSubmission(stepOrder);

	const fetchApplicationData = async (appId) => {
		try {
			const token = sessionStorage.getItem('token');

			const response = await axios.get(`${BASE_URL}/api/application/${appId}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (response.data.success && response.data.data) {
				const savedData = response.data.data;

				// Extract and store application status
				const status = savedData.status;
				console.log('📊 Application status:', status);
				setApplicationStatus(status);

				// Use utility function to map application data
				const processedData = mapApplicationData(savedData);

				setStepData((prev) => ({
					...prev,
					...processedData,
				}));

				setOriginalLoadedData(processedData);

				// Clear modified steps when loading data from server
				// (initial load should not be considered a modification)
				setModifiedSteps(new Set());

				return true;
			} else {
				console.log(' API response indicates failure or no data');
				return false;
			}
		} catch (err) {
			console.error(' Error fetching application data:', err);
			console.error(' Error details:', err.response?.data || err.message);
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	// Load saved data when component mounts
	useEffect(() => {
		const loadSavedData = async () => {
			if (applicationId && applicationId !== 'new') {
				await fetchApplicationData(applicationId);
			} else {
				console.log(' No applicationId or new application, skipping data load');
				setIsLoading(false);
			}
		};

		loadSavedData();
	}, [applicationId]);

	// Handle redirect for "Sent for Annexure" status
	useEffect(() => {
		if (!isLoading && applicationStatus === 'Sent for Annexure') {
			console.log('🔄 Redirecting to submit documents page...');
			navigate('/submitdocuments');
		}
	}, [isLoading, applicationStatus, navigate]);

	// Fetch application data when navigating to preview step to get all uploaded file URLs
	useEffect(() => {
		const refreshPreviewData = async () => {
			// Step 6 is the preview step (index 6 in 0-based array)
			if (currentStep === 6 && applicationId && applicationId !== 'new') {
				console.log('📋 Preview step reached - fetching latest application data including file URLs');
				setIsLoading(true);
				await fetchApplicationData(applicationId);
			}
		};

		refreshPreviewData();
	}, [currentStep, applicationId]);

	const handleNext = () => {
		if (currentStep === steps.length - 1) {
			// Final step, submit and show Thank You
			setIsSubmitted(true);
			sessionStorage.removeItem('applicationId'); // Removes application id from local storage
			sessionStorage.removeItem('userData');
			sessionStorage.removeItem('status');
			// Do NOT increment currentStep
		} else {
			setCurrentStep((prev) => prev + 1);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep((prev) => prev - 1);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleSave = async (isSavePressed = false) => {
		setIsSaving(true);
		setFullPageLoader(true);
		try {
			const success = await saveForm(
				stepData,
				applicationId,
				setApplicationId,
				originalLoadedData,
				currentStep,
				isSavePressed
			);
			if (success) {
				// Clear modified steps after successful save
				setModifiedSteps(new Set());
			}
			setIsSaving(false);
			setFullPageLoader(false);
			return success;
		} catch (error) {
			setIsSaving(false);
			setFullPageLoader(false);
			return false;
		}
	};

	const handleStepDataChange = (stepName, data) => {
		console.log(`🔍 Step data change for ${stepName}:`, data);

		// Special handling for education to preserve existing file URLs
		if (stepName === 'educationalBackground') {
			// If the new data doesn't have a file but the previous data does, preserve it
			if (!data.file && stepData.educationalBackground?.file) {
				data.file = stepData.educationalBackground.file;
			}
		}

		setStepData((prev) => {
			const prevData = prev[stepName];
			
			console.log(`📊 Comparing data for ${stepName}:`, {
				prevData,
				newData: data,
				prevDataType: typeof prevData,
				newDataType: typeof data
			});
			
			// If previous data doesn't exist, this is always a new change
			if (!prevData || Object.keys(prevData).length === 0) {
				console.log(`✅ Change detected: Previous data is empty or doesn't exist`);
				
				// Mark this step as modified
				setModifiedSteps((prevModified) => {
					const newModified = new Set(prevModified);
					newModified.add(stepName);
					console.log(`📝 Modified steps:`, Array.from(newModified));
					return newModified;
				});
				
				return {
					...prev,
					[stepName]: data,
				};
			}
			
			// Deep comparison function that handles Files, null, and objects
			const deepCompare = (obj1, obj2, path = '') => {
				console.log(`  🔎 Comparing at path '${path}':`, { obj1, obj2 });
				
				// Handle exact equality first (includes null === null)
				if (obj1 === obj2) {
					console.log(`    ✓ Equal by reference at '${path}'`);
					return true;
				}
				
				// If one is null/undefined and the other isn't, they're different
				if (obj1 == null || obj2 == null) {
					console.log(`    ✗ Null mismatch at '${path}':`, { obj1, obj2 });
					return false;
				}
				
				// Handle File objects - compare by name, size, and lastModified
				if (obj1 instanceof File && obj2 instanceof File) {
					const filesEqual = obj1.name === obj2.name && 
					       obj1.size === obj2.size && 
					       obj1.lastModified === obj2.lastModified;
					console.log(`    ${filesEqual ? '✓' : '✗'} File comparison at '${path}':`, { 
						obj1: { name: obj1.name, size: obj1.size }, 
						obj2: { name: obj2.name, size: obj2.size } 
					});
					return filesEqual;
				}
				// If one is a File and the other is not (including string URLs or null), they're different
				if (obj1 instanceof File || obj2 instanceof File) {
					console.log(`    ✗ File type mismatch at '${path}':`, { 
						obj1Type: obj1?.constructor.name, 
						obj2Type: obj2?.constructor.name 
					});
					return false;
				}
				
				// Handle arrays
				if (Array.isArray(obj1) && Array.isArray(obj2)) {
					if (obj1.length !== obj2.length) {
						console.log(`    ✗ Array length mismatch at '${path}':`, { 
							obj1Length: obj1.length, 
							obj2Length: obj2.length 
						});
						return false;
					}
					const arraysEqual = obj1.every((item, index) => deepCompare(item, obj2[index], `${path}[${index}]`));
					console.log(`    ${arraysEqual ? '✓' : '✗'} Array comparison at '${path}'`);
					return arraysEqual;
				}
				if (Array.isArray(obj1) || Array.isArray(obj2)) {
					console.log(`    ✗ Array type mismatch at '${path}'`);
					return false;
				}
				
				// Handle plain objects
				if (typeof obj1 === 'object' && typeof obj2 === 'object') {
					const keys1 = Object.keys(obj1);
					const keys2 = Object.keys(obj2);
					
					// Get all unique keys from both objects
					const allKeys = new Set([...keys1, ...keys2]);
					
					console.log(`    🔑 Object keys at '${path}':`, { 
						keys1, 
						keys2, 
						allKeys: Array.from(allKeys) 
					});
					
					// Check if all keys have equal values
					for (const key of allKeys) {
						if (!deepCompare(obj1[key], obj2[key], `${path}.${key}`)) {
							console.log(`    ✗ Object property mismatch at '${path}.${key}'`);
							return false;
						}
					}
					console.log(`    ✓ All object properties equal at '${path}'`);
					return true;
				}
				
				// Handle primitives
				const primitivesEqual = obj1 === obj2;
				console.log(`    ${primitivesEqual ? '✓' : '✗'} Primitive comparison at '${path}':`, { obj1, obj2 });
				return primitivesEqual;
			};
			
			const hasChanged = !deepCompare(prevData, data, stepName);
			
			console.log(`🎯 Final result for ${stepName}: hasChanged = ${hasChanged}`);

			if (!hasChanged) {
				console.log(`⏭️ No changes detected for ${stepName} - returning previous state`);
				return prev;
			}

			console.log(`✏️ Changes detected for ${stepName}, marking as modified`);

			// Mark this step as modified
			setModifiedSteps((prevModified) => {
				const newModified = new Set(prevModified);
				newModified.add(stepName);
				console.log(`📝 Modified steps:`, Array.from(newModified));
				return newModified;
			});

			return {
				...prev,
				[stepName]: data,
			};
		});
	};

	const handleSaveWithValidation = () => {
		if (currentStep === 0 && personalInfoRef.current && personalInfoRef.current.validate) {
			if (!personalInfoRef.current.validate()) {
				return;
			}
		}
		if (currentStep === 1 && educationRef.current && educationRef.current.validate) {
			if (!educationRef.current.validate()) {
				return;
			}
			if (educationRef.current && educationRef.current.getFormData) {
				handleStepDataChange('educationalBackground', educationRef.current.getFormData());
			}
		}
		if (currentStep === 2 && professionalExperienceRef.current && professionalExperienceRef.current.validate) {
			if (!professionalExperienceRef.current.validate()) {
				return;
			}
			if (professionalExperienceRef.current && professionalExperienceRef.current.getFormData) {
				handleStepDataChange('professionalExperience', professionalExperienceRef.current.getFormData());
			}
		}

		if (currentStep === 4 && recommendationsRef.current && recommendationsRef.current.validate) {
			if (!recommendationsRef.current.validate()) {
				return;
			}
			if (recommendationsRef.current && recommendationsRef.current.getFormData) {
				handleStepDataChange('recommendations', recommendationsRef.current.getFormData());
			}
		}
		if (currentStep === 3 && fellowshipInfoRef.current && fellowshipInfoRef.current.validate) {
			if (!fellowshipInfoRef.current.validate()) {
				return;
			}
			if (fellowshipInfoRef.current && fellowshipInfoRef.current.getFormData) {
				handleStepDataChange('fellowshipInformation', fellowshipInfoRef.current.getFormData());
			}
		}
		if (currentStep === 5 && additionalInformationRef.current && additionalInformationRef.current.validate) {
			if (!additionalInformationRef.current.validate()) {
				return;
			}
			if (additionalInformationRef.current && additionalInformationRef.current.getFormData) {
				handleStepDataChange('additionalInformation', additionalInformationRef.current.getFormData());
			}
		}
		console.log('Click handle Save with validation');
		handleSave(true);
	};

	// Combined function: Save and then move to next step
	const handleSaveAndNext = async () => {
		setGeneralError(''); // Clear previous error
		let errorMsg = '';

		const currentStepKey = stepOrder[currentStep];

		// Validate current step
		if (currentStep === 0) {
			if (!personalInfoRef.current.validate()) {
				errorMsg = 'Please fill all required personal information fields, including image and CV.';
			}
		}
		if (currentStep === 1) {
			if (educationRef.current && educationRef.current.validate && !educationRef.current.validate()) {
				errorMsg = 'Please fill all required educational background fields and upload certificate.';
			}
			if (educationRef.current && educationRef.current.getFormData) {
				handleStepDataChange('educationalBackground', educationRef.current.getFormData());
			}
		}
		if (currentStep === 2) {
			if (
				professionalExperienceRef.current &&
				professionalExperienceRef.current.validate &&
				!professionalExperienceRef.current.validate()
			) {
				errorMsg = 'Please fill all required professional experience fields.';
			}
			if (professionalExperienceRef.current && professionalExperienceRef.current.getFormData) {
				handleStepDataChange('professionalExperience', professionalExperienceRef.current.getFormData());
			}
		}
		if (currentStep === 4) {
			if (recommendationsRef.current && recommendationsRef.current.validate && !recommendationsRef.current.validate()) {
				errorMsg = 'Please fill all required recommendation fields and upload letters.';
			}
			if (recommendationsRef.current && recommendationsRef.current.getFormData) {
				handleStepDataChange('recommendations', recommendationsRef.current.getFormData());
			}
		}
		if (currentStep === 3) {
			// Validate Fellowship Information
			if (fellowshipInfoRef.current && fellowshipInfoRef.current.validate) {
				if (!fellowshipInfoRef.current.validate()) {
					errorMsg = 'Please fill all required fellowship information fields.';
				}
			}
			// Get form data to ensure it's up to date
			if (fellowshipInfoRef.current && fellowshipInfoRef.current.getFormData) {
				handleStepDataChange('fellowshipInformation', fellowshipInfoRef.current.getFormData());
			}
		}
		if (currentStep === 5) {
			if (
				additionalInformationRef.current &&
				additionalInformationRef.current.validate &&
				!additionalInformationRef.current.validate()
			) {
				errorMsg = 'Please fill all required additional information fields and upload documents.';
			}
			if (additionalInformationRef.current && additionalInformationRef.current.getFormData) {
				handleStepDataChange('additionalInformation', additionalInformationRef.current.getFormData());
			}
		}
		// if (currentStep === 6) {
		// 	// Preview step - no validation needed, just submit
		// 	await handleSubmit();
		// 	return;
		// }

		// If validation fails, show error and stop
		if (errorMsg) {
			setGeneralError(errorMsg);
			return;
		}

		// Check if current step has been modified
		const hasModifications = modifiedSteps.has(currentStepKey);

		// Only save if there are modifications
		if (hasModifications) {
			console.log(`💾 Saving step ${currentStepKey} - modifications detected`);
			const saveSuccess = await handleSave();
			
			// If save failed, don't move to next step
			if (!saveSuccess) {
				setGeneralError('Failed to save data. Please try again.');
				return;
			}
		} else {
			console.log(`⏭️ Skipping save for step ${currentStepKey} - no modifications`);
		}

		// After successful save (or skip), move to next step
		if (currentStep === steps.length - 1) {
			// Don't set isSubmitted here for preview step
			// Just move to next step
			setCurrentStep((prev) => prev + 1);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			setCurrentStep((prev) => prev + 1);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleSubmit = async () => {
		// Helper function to format date as YYYY-MM-DD without timezone conversion
		const formatDateForServer = (date) => {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		};
		
		// Validate all steps before submit
		if (personalInfoRef.current && personalInfoRef.current.validate && !personalInfoRef.current.validate()) {
			toast.error('Please complete all required fields in Personal Information.');
			return;
		}
		if (educationRef.current && educationRef.current.validate && !educationRef.current.validate()) {
			toast.error('Please complete all required fields in Educational Background.');
			return;
		}
		if (
			professionalExperienceRef.current &&
			professionalExperienceRef.current.validate &&
			!professionalExperienceRef.current.validate()
		) {
			toast.error('Please complete all required fields in Professional Experience.');
			return;
		}
		if (recommendationsRef.current && recommendationsRef.current.validate && !recommendationsRef.current.validate()) {
			toast.error('Please complete all required fields in Recommendations.');
			return;
		}
		if (fellowshipInfoRef.current && fellowshipInfoRef.current.handleNextWithValidation) {
			// If FellowshipInformation validation fails, block submit
			let valid = true;
			const originalOnNext = fellowshipInfoRef.current.onNext;
			fellowshipInfoRef.current.onNext = () => {
				valid = false;
			};
			fellowshipInfoRef.current.handleNextWithValidation();
			fellowshipInfoRef.current.onNext = originalOnNext;
			if (!valid) {
				toast.error('Please complete all required fields in Fellowship Information.');
				return;
			}
		}
		if (
			additionalInformationRef.current &&
			additionalInformationRef.current.validate &&
			!additionalInformationRef.current.validate()
		) {
			toast.error('Please complete all required fields in Additional Information.');
			return;
		}
		setIsSaving(true);
		setFullPageLoader(true);
		try {
			const token = sessionStorage.getItem('token');
			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			};
			const formDataToSend = new FormData();

			if (applicationId) {
				formDataToSend.append('applicationId', applicationId);
			}
			// ALWAYS process education data if we have original data, regardless of current stepData
			const education = stepData.educationalBackground;
			if (education && Object.keys(education).length > 0) {
				Object.entries(education).forEach(([key, value]) => {
					// Skip file objects as they need special handling
					if (key === 'file') return;

					let sanitizedValue = value === null ? '' : value;
					if (sanitizedValue instanceof Date) {
						sanitizedValue = formatDateForServer(sanitizedValue);
					} else if (
						typeof sanitizedValue === 'object' &&
						sanitizedValue !== null &&
						!(sanitizedValue instanceof File)
					) {
						sanitizedValue = JSON.stringify(sanitizedValue);
					}
					formDataToSend.append(`education_${key}`, sanitizedValue);
				});

				// Handle education file
				let fileToUse = education.file;
				// console.log(` Education file to process (submit):`, fileToUse);

				if (fileToUse && fileToUse instanceof File) {
					// console.log("🎓 Adding education_file (submit):", fileToUse.name);
					formDataToSend.append('education_file', fileToUse);
				} else if (fileToUse && typeof fileToUse === 'string') {
					// console.log(
					//   "🎓 Preserving existing certificate URL (submit):",
					//   fileToUse
					// );
					formDataToSend.append('certificate_url', fileToUse);
				} else {
					console.log(' No education file or URL found (submit)');
				}
			}
			for (let i = 0; i < stepOrder.length; i++) {
				const stepKey = stepOrder[i];
				const currentStepData = stepData[stepKey];
				// Skip education step since we already processed it above
				if (stepKey === 'educationalBackground') {
					// console.log(
					//   `⏭️ Skipping education step in submit - already processed above`
					// );
					continue;
				}
				// Skip if no data exists for this step
				if (!currentStepData || Object.keys(currentStepData).length === 0) {
					// console.log(
					//   `⚠️ Submit - Skipping step ${stepKey} - no data or empty object`
					// );

					// Special handling for education in submit - if we have original data, use it
					if (stepKey === 'educationalBackground' && originalLoadedData.educationalBackground) {
						// console.log(
						//   `🔧 Submit - Using original education data as fallback`
						// );
						const fallbackData = originalLoadedData.educationalBackground;

						// Process education data from fallback
						Object.entries(fallbackData).forEach(([key, value]) => {
							if (key === 'file') return;
							let sanitizedValue = value === null ? '' : value;
							if (sanitizedValue instanceof Date) {
								sanitizedValue = formatDateForServer(sanitizedValue);
							} else if (
								typeof sanitizedValue === 'object' &&
								sanitizedValue !== null &&
								!(sanitizedValue instanceof File)
							) {
								sanitizedValue = JSON.stringify(sanitizedValue);
							}
							formDataToSend.append(`education_${key}`, sanitizedValue);
						});

						// Handle education file from fallback
						let fileToUse = fallbackData.file;
						if (fileToUse && fileToUse instanceof File) {
							// console.log(
							//   "🎓 Submit - Adding education_file from fallback:",
							//   fileToUse.name
							// );
							formDataToSend.append('education_file', fileToUse);
						} else if (fileToUse && typeof fileToUse === 'string') {
							// console.log(
							//   "🎓 Submit - Preserving existing certificate URL from fallback:",
							//   fileToUse
							// );
							formDataToSend.append('certificate_url', fileToUse);
						}
					}
					continue;
				}
				switch (stepKey) {
					case 'personalInfo':
						if (currentStepData.personalInfo) {
							Object.entries(currentStepData.personalInfo).forEach(([key, value]) => {
								if (key === 'applicationId') return;
								if (key === 'mailingAddress' && typeof value === 'object' && value !== null) {
									formDataToSend.append('mailingAddress_address', value.address || '');
									formDataToSend.append('mailingAddress_city', value.city || '');
									formDataToSend.append('mailingAddress_state', value.state || '');
									formDataToSend.append('mailingAddress_country', value.country || '');
									formDataToSend.append('mailingAddress_zipCode', value.zipCode || '');
									return;
								}
								if (key === 'permanentAddress' && typeof value === 'object' && value !== null) {
									formDataToSend.append('permanentAddress_address', value.address || '');
									formDataToSend.append('permanentAddress_city', value.city || '');
									formDataToSend.append('permanentAddress_state', value.state || '');
									formDataToSend.append('permanentAddress_country', value.country || '');
									formDataToSend.append('permanentAddress_zipCode', value.zipCode || '');
									return;
								}
								let sanitizedValue = value === null ? '' : value;
								if (sanitizedValue instanceof Date) {
									sanitizedValue = formatDateForServer(sanitizedValue);
								} else if (
									typeof sanitizedValue === 'object' &&
									sanitizedValue !== null &&
									!(sanitizedValue instanceof File)
								) {
									sanitizedValue = JSON.stringify(sanitizedValue);
								}
								formDataToSend.append(`personalInfo_${key}`, sanitizedValue);
							});
						}
						if (currentStepData.image && currentStepData.image instanceof File) {
							formDataToSend.append('personalInfo_photo', currentStepData.image);
						}
						if (currentStepData.document && currentStepData.document instanceof File) {
							formDataToSend.append('personalInfo_document', currentStepData.document);
						}
						break;
					case 'educationalBackground':
						Object.entries(currentStepData).forEach(([key, value]) => {
							if (key === 'file') return;
							let sanitizedValue = value === null ? '' : value;
							if (sanitizedValue instanceof Date) {
								sanitizedValue = formatDateForServer(sanitizedValue);
							} else if (
								typeof sanitizedValue === 'object' &&
								sanitizedValue !== null &&
								!(sanitizedValue instanceof File)
							) {
								sanitizedValue = JSON.stringify(sanitizedValue);
							}
							formDataToSend.append(`education_${key}`, sanitizedValue);
						});
						// Add education file separately - preserve existing URL if no new file is uploaded
						let fileToUse = currentStepData.file;
						if (!fileToUse && originalLoadedData.educationalBackground?.file) {
							fileToUse = originalLoadedData.educationalBackground.file;
						}

						if (fileToUse && fileToUse instanceof File) {
							// console.log(" Adding education_file:", fileToUse.name);
							formDataToSend.append('education_file', fileToUse);
						} else if (fileToUse && typeof fileToUse === 'string') {
							// Preserve existing certificate URL when no new file is uploaded
							// console.log(" Preserving existing certificate URL:", fileToUse);
							formDataToSend.append('certificate_url', fileToUse);
						} else {
							// console.log(" No education file or URL to preserve in submit");
						}
						break;
					case 'professionalExperience':
						Object.entries(currentStepData).forEach(([key, value]) => {
							let sanitizedValue = value === null ? '' : value;
							if (sanitizedValue instanceof Date) {
								sanitizedValue = formatDateForServer(sanitizedValue);
							} else if (
								typeof sanitizedValue === 'object' &&
								sanitizedValue !== null &&
								!(sanitizedValue instanceof File)
							) {
								sanitizedValue = JSON.stringify(sanitizedValue);
							}
							formDataToSend.append(`experience_${key}`, sanitizedValue);
						});
						break;
					case 'recommendations':
						if (currentStepData.recommendation1) {
							Object.entries(currentStepData.recommendation1).forEach(([key, value]) => {
								if (key === 'file') return;
								let sanitizedValue = value === null ? '' : value;
								if (sanitizedValue instanceof Date) {
									sanitizedValue = formatDateForServer(sanitizedValue);
								} else if (
									typeof sanitizedValue === 'object' &&
									sanitizedValue !== null &&
									!(sanitizedValue instanceof File)
								) {
									sanitizedValue = JSON.stringify(sanitizedValue);
								}
								formDataToSend.append(`recommendation1_${key}`, sanitizedValue);
							});
							if (currentStepData.recommendation1.file && currentStepData.recommendation1.file instanceof File) {
								// console.log(
								//   "📝 Adding recommendation1_letter:",
								//   currentStepData.recommendation1.file.name
								// );
								formDataToSend.append('recommendation1_letter', currentStepData.recommendation1.file);
							} else if (
								currentStepData.recommendation1.file &&
								typeof currentStepData.recommendation1.file === 'string'
							) {
								// Preserve existing letter URL when no new file is uploaded
								// console.log(
								//   " Preserving existing recommendation1 letter URL:",
								//   currentStepData.recommendation1.file
								// );
								formDataToSend.append('recommendation1_letter_url', currentStepData.recommendation1.file);
							}
						}
						if (currentStepData.recommendation2) {
							Object.entries(currentStepData.recommendation2).forEach(([key, value]) => {
								if (key === 'file') return;
								let sanitizedValue = value === null ? '' : value;
								if (sanitizedValue instanceof Date) {
									sanitizedValue = formatDateForServer(sanitizedValue);
								} else if (
									typeof sanitizedValue === 'object' &&
									sanitizedValue !== null &&
									!(sanitizedValue instanceof File)
								) {
									sanitizedValue = JSON.stringify(sanitizedValue);
								}
								formDataToSend.append(`recommendation2_${key}`, sanitizedValue);
							});
							if (currentStepData.recommendation2.file && currentStepData.recommendation2.file instanceof File) {
								formDataToSend.append('recommendation2_letter', currentStepData.recommendation2.file);
							} else if (
								currentStepData.recommendation2.file &&
								typeof currentStepData.recommendation2.file === 'string'
							) {
								formDataToSend.append('recommendation2_letter_url', currentStepData.recommendation2.file);
							}
						}
						break;

					case 'fellowshipInformation':
						// Fellowship Information (Step 4)
						if (currentStepData.programAppliedFor) {
							formDataToSend.append('fellowship_program', currentStepData.programAppliedFor);
						}

						// console.log(currentStepData.themes, "current step data themes");
						if (currentStepData.themes && typeof currentStepData.themes === 'object') {
							const validThemes = Object.entries(currentStepData.themes).filter(
								([key, value]) => value !== null && value !== undefined
							);

							const selectedThemeKeys = validThemes.filter(([key, value]) => value === true).map(([key]) => key);
							//   console.log(selectedThemeKeys,"slecte");
							if (selectedThemeKeys) {
								formDataToSend.append('fellowship_themes', JSON.stringify(selectedThemeKeys));
							}
						}
						if (
							currentStepData.themes &&
							currentStepData.themes.others &&
							currentStepData.othersField &&
							currentStepData.othersField.trim()
						) {
							formDataToSend.append('fellowship_othersField', currentStepData.othersField.trim());
						}
						// Add fellowship files separately - preserve existing URLs if no new files are uploaded
						if (currentStepData.bookProposal && currentStepData.bookProposal instanceof File) {
							formDataToSend.append('fellowship_bookProposal', currentStepData.bookProposal);
						} else if (currentStepData.bookProposal && typeof currentStepData.bookProposal === 'string') {
							formDataToSend.append('fellowship_bookProposal_url', currentStepData.bookProposal);
						}
						if (currentStepData.writingSample && currentStepData.writingSample instanceof File) {
							formDataToSend.append('fellowship_writingSample', currentStepData.writingSample);
						} else if (currentStepData.writingSample && typeof currentStepData.writingSample === 'string') {
							formDataToSend.append('fellowship_writingSample_url', currentStepData.writingSample);
						}
						break;
					case 'additionalInformation':
						// Handle publications with file uploads
						if (Array.isArray(currentStepData.publications)) {
							const validPublications = currentStepData.publications
								.filter((pub) => pub.name && pub.name.trim()) // Only require name
								.map((pub) => ({
									name: pub.name.trim(),
									url: pub.url ? pub.url.trim() : '',
									file_url: pub.file_url || '', // For previously uploaded files
								}));
							if (validPublications.length > 0) {
								formDataToSend.append('publications', JSON.stringify(validPublications));
							}
						}

						// Handle publication files separately
						if (Array.isArray(currentStepData.publications)) {
							currentStepData.publications.forEach((pub, index) => {
								if (pub.file && pub.file instanceof File) {
									formDataToSend.append(`additional_additionalDocument`, pub.file);
								}
							});
						}

						// Handle social media profiles
						if (Array.isArray(currentStepData.socialMedia)) {
							const validSocial = currentStepData.socialMedia
								.filter((soc) => soc.platform && soc.platform.trim() && soc.url && soc.url.trim())
								.map((soc) => ({
									platform: soc.platform.trim(),
									url: soc.url.trim(),
								}));
							if (validSocial.length > 0) {
								formDataToSend.append('socialMedia', JSON.stringify(validSocial));
							}
						}

						// Handle additional document
						if (currentStepData.additionalDocument && currentStepData.additionalDocument instanceof File) {
							formDataToSend.append('additional_document', currentStepData.additionalDocument);
						}

						// Handle terms acceptance
						if (currentStepData.termsAccepted) {
							formDataToSend.append('terms_accepted', 'true');
						}
						break;
					default:
						// Add a default case to satisfy ESLint
						break;
				}
			}
			const response = await axios.post(`${BASE_URL}/api/application/save`, formDataToSend, config);
			if (response?.data?.success) {
				// After successful save, call submit API
				try {
					const submitRes = await axios.post(
						`${BASE_URL}/api/application/${applicationId}/submit`,
						{},
						{ headers: { Authorization: `Bearer ${token}` } }
					);
					if (submitRes?.data?.success) {
						setIsSubmitted(true);
						toast.success('Application submitted successfully!');
					} else {
						throw new Error(submitRes?.data?.message || 'Failed to submit application');
					}
				} catch (submitErr) {
					toast.error(
						`Application saved, but failed to submit: ${submitErr.response?.data?.message || submitErr.message}`
					);
				}
			} else {
				throw new Error('Failed to submit application');
			}
		} catch (err) {
			toast.error(`Failed to submit application: ${err.response?.data?.message || err.message}`);
		} finally {
			setIsSaving(false);
			setFullPageLoader(false);
		}
	};

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const containers = document.querySelectorAll('.StepContainer-0-2-2');
		containers.forEach((el, i) => {
			// Step is "done" only if it's strictly before the current step
			el.classList.toggle('step-done', i < currentStep);
			el.classList.toggle('step-active', i === currentStep);
		});
	}, [currentStep]);

	const handlePersonalInfoParsed = (parsedData) => {
		setResumeData(parsedData);
		// console.log("Parsed personal info data from child:", parsedData);
		// You can store it in parent state or further process it here
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 0:
				return (
					<PersonalInformation
						ref={personalInfoRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.personalInfo}
						onDataChange={(data) => handleStepDataChange('personalInfo', data)}
						onParsedData={handlePersonalInfoParsed}
					/>
				);
			case 1:
				return (
					<EducationalBackground
						ref={educationRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.educationalBackground}
						onDataChange={(data) => handleStepDataChange('educationalBackground', data)}
						resumeData={resumeData}
					/>
				);
			case 2:
				return (
					<ProfessionalExperience
						ref={professionalExperienceRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.professionalExperience}
						onDataChange={(data) => handleStepDataChange('professionalExperience', data)}
						resumeData={resumeData}
					/>
				);
			case 3:
				return (
					<FellowshipInformation
						ref={fellowshipInfoRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.fellowshipInformation}
						onDataChange={(data) => handleStepDataChange('fellowshipInformation', data)}
						resumeData={resumeData}
					/>
				);
			case 4:
				return (
					<Recommendations
						ref={recommendationsRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.recommendations}
						onDataChange={(data) => handleStepDataChange('recommendations', data)}
						resumeData={resumeData}
					/>
				);
			case 5:
				return (
					<AdditionalInformation
						ref={additionalInformationRef}
						applicationId={applicationId}
						onNext={handleNext}
						onSave={handleSave}
						data={stepData.additionalInformation}
						onDataChange={(data) => handleStepDataChange('additionalInformation', data)}
						resumeData={resumeData}
					/>
				);
			case 6:
				return <PreviewPage ref={previewRef} data={stepData} onSubmit={handleSubmit} onBack={handlePrevious} />;
			default:
				return null;
		}
	};

	return (
		<>
			{fullPageLoader && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						background: 'rgba(255,255,255,0.7)',
						zIndex: 9999,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
					<div className="spinner-border" style={{ width: 60, height: 60 }} role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			)}
			<section className="form_section">
				<ToastContainer position="bottom-right" autoClose={3000} />
				<div className="container container_cust">
					<h1 className="fellow_memberhead">Fellowship Application Form</h1>
					<div className="custom_wraaper">
						{isLoading ? (
							<div className="text-center py-5">
								<div className="spinner-border" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
								<p className="mt-3">Loading your application...</p>
							</div>
						) : isSubmitted ? (
							<ThankYou />
						) : applicationStatus && applicationStatus !== 'Draft' && applicationStatus !== 'Sent for Annexure' ? (
							<ApplicationStatus status={applicationStatus} />
						) : (
							<>
								{/* Stepper always visible */}
								<Stepper
									activeStep={currentStep}
									className="custom-stepper"
									styleConfig={{
										connectorColor: '#e0e0e0',
										size: '2em',
										circleFontSize: '1em',
									}}
									stepClassName="custom-step">
									{steps.map((step, index) => (
										<Step
											key={index}
											label={step.label}
											onClick={currentStep > index ? () => setCurrentStep(index) : null}
											children={
												index < currentStep ? (
													<img src="/assets/images/tickwhite.svg" alt="tick" />
												) : index === currentStep ? (
													<span className="current-circle">●</span>
												) : (
													<span className="blank-circle">{index + 1}</span>
												)
											}
										/>
									))}
								</Stepper>

								<div className="step_div">
									<div className="step-content">{renderStepContent()}</div>

									{/* Hide buttons for preview step (step 6) as it has its own buttons */}
									{currentStep !== 6 && (
										<div className="btnform_flex">
											<button
												className={['prev_btn', currentStep === 0 && 'disabled_btn'].filter(Boolean).join(' ')}
												onClick={handlePrevious}
												disabled={currentStep === 0 || isSaving}>
												Back
											</button>

											<button className={'next_btn'} onClick={handleSaveAndNext} disabled={isSaving}>
												{isSaving ? (
													<>
														<span
															className="spinner-border spinner-border-sm me-2"
															role="status"
															aria-hidden="true"></span>
														Saving...
													</>
												) : currentStep === steps.length - 1 ? (
													'Submit'
												) : (
													'Next'
												)}
											</button>
											<button className={'save_form'} onClick={handleSaveWithValidation} disabled={isSaving}>
												{isSaving ? 'Saving...' : 'Save Form'}
											</button>
										</div>
									)}
								</div>
							</>
						)}
					</div>
					{generalError && (
						<div style={{ color: 'red', marginTop: '4px', textAlign: 'left', fontWeight: '' }}>{generalError}</div>
					)}
				</div>
			</section>
		</>
	);
};

export default Form;
